import { assetPath, getBasePath } from './getBasePath';

const SERVICE_WORKER_PATH = '/notifications-sw.js';

export const isDesktopNotificationSupported = (): boolean =>
    typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

/**
 * Registers the notifications service worker.
 *
 * Only a service worker can show a notification that outlives the page: an in-page
 * `new Notification()` is closed as soon as Chrome discards the frozen tab.
 */
export const registerNotificationsServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
    if (!isDesktopNotificationSupported()) {
        return null;
    }

    try {
        // Ephemeral environments serve the app from `/frontend-<id>`, so both the script URL
        // and the scope have to carry that prefix
        return await navigator.serviceWorker.register(assetPath(SERVICE_WORKER_PATH), {
            scope: `${getBasePath()}/`,
        });
    } catch (error) {
        console.error('Failed to register notifications service worker', error);
        return null;
    }
};

/**
 * Asks for notification permission. Must be called from a user gesture - Chrome suppresses
 * the prompt otherwise, and a denied permission cannot be re-requested from code.
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    if (!isDesktopNotificationSupported()) {
        return 'denied';
    }

    if (Notification.permission !== 'default') {
        return Notification.permission;
    }

    return Notification.requestPermission();
};

/**
 * Shows a notification through the service worker.
 *
 * Deliberately not awaited: callers fire this from `freeze`, where the page stops executing
 * before the promise can settle. The worker still receives the request.
 */
export const showPersistentNotification = (
    registration: ServiceWorkerRegistration | null,
    title: string,
    options?: NotificationOptions
): void => {
    if (!registration || !isDesktopNotificationSupported() || Notification.permission !== 'granted') {
        return;
    }

    void registration.showNotification(title, options);
};
